import { Theme, createTheme } from "@mui/material/styles";
import React from "react";
import { ptBR as datePickerPtBr } from "@mui/x-date-pickers/locales";
import { ptBR as corePtBr } from "@mui/material/locale";
// export const themeColors = {
//   bottomColor: "#242C35",
//   fontColor: "#ffffff",
//   primary: "#14ed01",
//   primaryFont: "#ffffff",
//   secondary: "#1FA",
//   bgGradient: "linear-gradient(180deg, #14ED01 0%, #1FA214 100%)"
// };

// export const muiTheme = createTheme({
//   palette: {
//     primary: { main: themeColors.primary },
//     ...((({ primary, secondary,...remaining}) => remaining)(themeColors))
//   },
// });

type ThemeModes = "light" | "dark";

export const themeColors = {
  light: {
    bottomColor: "#1A1D23", // Mantendo escuro mesmo no tema light
    backgroundColor: "#242C35",
    selectColor: "#2e2e2e",
    inputColor: "#333333",
    topElementsColor: "#383838",
    fontColor: "#ffffff",
    secondaryFont: "#C2C2C2",
    primary: "#04e762", // Verde vibrante
    primaryFont: "#ffffff",
    secondary: "#01a043", // Verde mais escuro
    awayColor: "#1B73DA",
    homeColor: "#2af497",
  },
  dark: {
    bottomColor: "#1A1D23",
    backgroundColor: "#242C35",
    selectColor: "#2e2e2e",
    inputColor: "#333333",
    topElementsColor: "#383838",
    fontColor: "#ffffff",
    secondaryFont: "#C2C2C2",
    primary: "#04e762", // Verde vibrante
    primaryFont: "#ffffff",
    secondary: "#01a043", // Verde mais escuro
    awayColor: "#1B73DA",
    homeColor: "#2af497",
  },
};

type MuiScheme = {
  light: string;
  dark: string;
  main: string;
};

type MuiThemeObj = {
  [Key in keyof typeof themeColors.light]: MuiScheme;
};

declare module "@mui/material/styles" {
  interface PaletteOptions extends Omit<MuiThemeObj, "primary" | "secondary"> { }

  interface Palette extends Omit<MuiThemeObj, "primary" | "secondary"> { }
}

export function ToggleColorModeProvider({
  children,
}: {
  children: JSX.Element;
}) {
  const [mode, setMode] = React.useState<ThemeModes>("dark");

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    []
  );

  const themeKeys = Object.keys(themeColors.light) as Array<
    keyof typeof themeColors.light
  >;

  const convertedThemeMui = React.useMemo(
    () =>
      themeKeys.reduce(
        (acc, curr) => {
          acc[curr] = {
            light: themeColors.light[curr],
            dark: themeColors.dark[curr],
            main: themeColors[mode][curr],
          };
          return acc;
        },
        {} as {
          [key in keyof typeof themeColors.light]: {
            [key in ThemeModes | "main"]: string;
          };
        }
      ),
    [mode]
  );

  const muiDefaultTheme = React.useMemo(
    () =>
      createTheme(
        {
          typography: {
            fontFamily: "Nunito, sans-serif",
          },
          palette: {
            mode,
            ...convertedThemeMui,
          },
          components: {
            MuiButton: {
              defaultProps: {
                variant: "contained"
              },
              variants: [
                {
                  props: { color: "error" },
                  style: {
                    background: "linear-gradient(90deg, #f44336, #a33027)",
                    border: "2px solid #621d17 !important",
                  },
                },
                {
                  props: { variant: "contained" },
                  style: {
                    border: "1px solid #04e762",
                    background:
                      "linear-gradient(90deg, #197640 0%, #01a043 100%)",
                    fontWeight: 700,
                  },
                },
                {
                  props: { variant: "text" },
                  style: {
                    border: `1px solid ${convertedThemeMui.fontColor.main}`,
                    transition: `background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
                    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
                    border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
                    color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
                    "&:hover": {
                      backgroundColor: `${convertedThemeMui.fontColor.main}20`,
                    },
                  },
                },
                {
                  props: { variant: "outlined" },
                  style: {
                    background: "#07401f",
                    padding: "6px 8px",
                  },
                },
              ],
            },
          },
        },
        corePtBr,
        datePickerPtBr
      ),
    [mode]
  );

  return (
    <ColorModeContext.Provider
      value={{
        toggleColorMode: colorMode.toggleColorMode,
        themeMui: muiDefaultTheme,
        currentTheme: mode,
      }}
    >
      {children}
    </ColorModeContext.Provider>
  );
}

const ColorModeContext = React.createContext({
  toggleColorMode: () => { },
  themeMui: {} as Theme,
  currentTheme: "" as ThemeModes,
});

export const useColorContext = () => {
  const colorContext = React.useContext(ColorModeContext);

  return colorContext;
};
